import uniq from 'lodash/uniq'

import { localTimezone, stringToDate } from 'modules/core/lib/dates'
import hasFeature from 'modules/core/lib/hasFeature'
import { apiClient as ApiClient, Collection, Model } from 'modules/core/lib/mobx-rest'

import { type ContractVersions } from 'modules/contracts/store/collections/contractVersions'
import employeesCollection from 'modules/employees/store/collections/employees'
import { EnabledFeaturesState } from 'modules/shift_management/hooks/reducers/enabledFeatures'
import {
  MAX_ITEMS_PER_PAGE,
  MAX_WORKAREAS_PER_PAGE,
} from 'modules/shift_management/hooks/reducers/pagination'
import buildEmployeeParamsFromResources from 'modules/shift_management/lib/buildEmployeeParamsFromResources'
import buildWorkAreaParamsFromResources, {
  WorkAreaPayload,
} from 'modules/shift_management/lib/buildWorkAreaParamsFromResources'
import {
  ApplyTemplateResponse,
  ConflictsProps,
  CopyScheduleResponse,
  CopyScheduleStatsResponse,
  EmployeePermissions,
  Filters,
  IExportShiftsSchedule,
  ShiftsRow,
  StatsResponse,
} from 'modules/shift_management/lib/types'
import copyScheduleStats from 'modules/shift_management/store/api/collections/copyScheduleStats'
import defaultShiftsCollection from 'modules/shift_management/store/api/collections/defaultShifts'
import employeesPermissionsCollection from 'modules/shift_management/store/api/collections/employeesPermissions'
import leavesCollection from 'modules/shift_management/store/api/collections/leaves'
import planningVersionsCollection from 'modules/shift_management/store/api/collections/planningVersions'
import restDaysCollection from 'modules/shift_management/store/api/collections/restDays'
import shiftsCollection from 'modules/shift_management/store/api/collections/shifts'
import statsCollection from 'modules/shift_management/store/api/collections/stats'
import templateWeeksCollection from 'modules/shift_management/store/api/collections/templateWeeks'
import unassignedShiftsCollection from 'modules/shift_management/store/api/collections/unassignedShifts'
import workAreasCollection from 'modules/shift_management/store/api/collections/workAreas'
import type EmployeePermission from 'modules/shift_management/store/api/models/EmployeePermission'
import DefaultShifts from 'modules/shift_management/store/internal/collections/DefaultShifts'
import Holidays from 'modules/shift_management/store/internal/collections/Holidays'
import Leaves from 'modules/shift_management/store/internal/collections/Leaves'
import RestDays from 'modules/shift_management/store/internal/collections/RestDays'
import Shifts from 'modules/shift_management/store/internal/collections/Shifts'
import TemplateWeeks from 'modules/shift_management/store/internal/collections/TemplateWeeks'
import UnassignedShifts from 'modules/shift_management/store/internal/collections/UnassignedShifts'
import { LeavePayload } from 'modules/shift_management/store/internal/models/Leave'
import { PlanningVersionPayload } from 'modules/shift_management/store/internal/models/PlanningVersion'
import { RestDayPayload } from 'modules/shift_management/store/internal/models/RestDay'
import { ShiftPayload } from 'modules/shift_management/store/internal/models/Shift'
import type { GroupType, ViewType } from 'modules/shift_management/store/internal/models/View'
import View, { GROUP_TYPES } from 'modules/shift_management/store/internal/models/View'
import companyHolidays from 'modules/timeoff/store/collections/companyHolidays'
import type CompanyHoliday from 'modules/timeoff/store/models/CompanyHoliday'

interface FetchParams {
  startAt: string
  endAt: string
  page?: number | null | undefined
  perPage?: number | null | undefined
  filters: Filters
  groupType: GroupType
  employeeIds?: number[] | null | undefined
  features: EnabledFeaturesState
  orderBy?: string | null
  orderDir?: string | null
}

interface FetchStatsParams {
  startAt: string
  endAt: string
  filters: Filters
  view: View
  employeeIds?: number[] | null | undefined
}

interface ApplyTemplateParams {
  selectedTemplateId: number
  weekPosition: number
  employeeIds: number[]
  viewStartDate: string
  viewEndDate: string
  startDateParam: string
  endDateParam: string
  notes: string
  locationId: number
  workAreaId: number
  timezone: string
}

interface DateParams {
  startAt: string | null | undefined
  endAt: string | null | undefined
}

interface UnassignedShiftParams extends DateParams {
  features: EnabledFeaturesState
  view: View
}

interface FetchResourcesResponse {
  workAreas?: WorkAreaPayload[]
  employeesPermissions: EmployeePermissions[]
  leaves: LeavePayload[]
  planningVersions: PlanningVersionPayload[]
  shifts: ShiftPayload[]
  holidays: CompanyHoliday[]
  restDays: RestDayPayload[]
}

interface FetchResponse {
  shiftsRows: ShiftsRow[]
  shifts: Shifts | undefined
  leaves: Leaves | undefined
  holidays: Holidays | undefined
  restDays: RestDays | undefined
}

class Api {
  static async fetch(
    params: FetchParams,
    contractVersions: ContractVersions
  ): Promise<FetchResponse> {
    switch (params.groupType) {
      case GROUP_TYPES.areas:
        return await Api.fetchWorkAreas(params, contractVersions)

      default:
        return await Api.fetchEmployees(params, contractVersions)
    }
  }

  static async fetchEmployees(
    params: FetchParams,
    contractVersions: ContractVersions
  ): Promise<FetchResponse> {
    const { employeesPermissions, leaves, planningVersions, shifts, holidays, restDays } =
      await Api.fetchResources({ ...params, perPage: MAX_ITEMS_PER_PAGE })

    return {
      shifts: Shifts.buildFromRequest(shifts),
      leaves: undefined,
      holidays: undefined,
      restDays: undefined,
      shiftsRows: buildEmployeeParamsFromResources(
        employeesPermissions,
        leaves,
        planningVersions,
        shifts,
        holidays,
        restDays,
        contractVersions
      ),
    }
  }

  static async fetchWorkAreas(
    params: FetchParams,
    contractVersions: ContractVersions
  ): Promise<FetchResponse> {
    const {
      workAreas,
      employeesPermissions,
      leaves,
      planningVersions,
      shifts,
      holidays,
      restDays,
    } = await Api.fetchResources({ ...params, perPage: MAX_WORKAREAS_PER_PAGE })

    const shiftsRows = buildWorkAreaParamsFromResources(
      workAreas,
      employeesPermissions,
      planningVersions,
      shifts,
      holidays,
      contractVersions
    )

    return {
      leaves: Leaves.buildFromProps(leaves),
      holidays: Holidays.buildFromProps(holidays),
      restDays: RestDays.buildFromProps(restDays),
      shifts: Shifts.buildFromRequest(shifts),
      shiftsRows,
    }
  }

  static async fetchStats(params: FetchStatsParams): Promise<StatsResponse> {
    const { startAt, endAt, employeeIds, filters } = params

    const [statsData] = await Promise.all([
      statsCollection.rpc('', {
        start_date: startAt,
        end_date: endAt,
        employee_ids: employeeIds,
        ...filters,
      }),
    ])

    return { stats: statsData }
  }

  static async fetchDay(
    dayId: string,
    filters: Filters,
    features: EnabledFeaturesState,
    contractVersions: ContractVersions
  ): Promise<FetchResponse> {
    const { employeesPermissions, leaves, planningVersions, shifts, holidays, restDays } =
      await Api.fetchResources({
        startAt: dayId,
        endAt: dayId,
        filters,
        groupType: GROUP_TYPES.employees,
        features,
      })

    return {
      shifts: undefined,
      leaves: undefined,
      holidays: undefined,
      restDays: undefined,
      shiftsRows: buildEmployeeParamsFromResources(
        employeesPermissions,
        leaves,
        planningVersions,
        shifts,
        holidays,
        restDays,
        contractVersions
      ),
    }
  }

  static async publish(
    startAt: string,
    endAt: string,
    filters: Filters,
    sendNotifications: boolean,
    viewType: ViewType
  ): Promise<void> {
    const api = ApiClient()

    return await api.post('/shift_management/schedules/publish', {
      start_at: startAt,
      end_at: endAt,
      ...filters,
      send_notifications: sendNotifications,
      view_type: viewType,
    })
  }

  static async applyTemplate(params: ApplyTemplateParams): Promise<ApplyTemplateResponse> {
    const api = ApiClient()

    return await api.post('/shift_management/schedules/apply_template', {
      view_start_date: params.viewStartDate,
      view_end_date: params.viewEndDate,
      template_id: params.selectedTemplateId,
      template_week_position: params.weekPosition,
      employees_ids: params.employeeIds,
      start_date: params.startDateParam,
      end_date: params.endDateParam,
      local_timezone: localTimezone(),
      notes: params.notes,
      work_area_id: params.workAreaId,
      location_id: params.locationId,
      timezone: params.timezone,
    })
  }

  static async exportShiftsSchedule(payload: IExportShiftsSchedule): Promise<void> {
    const api = ApiClient()
    const url = '/shift_management/schedules/exports'

    return await api.post(url, payload)
  }

  static async fetchCopyScheduleStats(
    originalStartAt: string,
    filters: Filters
  ): Promise<CopyScheduleStatsResponse> {
    const params = {
      original_start_at: originalStartAt,
      ...filters,
    }

    // FIXME: We do RPC because there are companies with too employee_ids (~900employees) and
    // with GET, it throws CORS error, but we should migrate this to backend filters.
    return await copyScheduleStats.rpc('', params)
  }

  static async copySchedule(
    originalStartAt: string,
    targetStartAt: string,
    filters: Filters
  ): Promise<CopyScheduleResponse> {
    const api = ApiClient()

    // Send days_to_copy to 7, 28 or the month's days to copy based on view (week, 4weeks, month)
    const params = {
      original_start_at: originalStartAt,
      target_start_at: targetStartAt,
      ...filters,
    }

    const url = '/shift_management/schedules/bulk_copy'

    return await api.post(url, params)
  }

  static async checkOverlappingShifts(
    selectedTemplateId: number,
    weekPosition: number,
    employeeIds: number[],
    startDateParam: string,
    endDateParam: string
  ): Promise<ConflictsProps[]> {
    const api = ApiClient()

    const params = {
      template_id: selectedTemplateId,
      template_week_position: weekPosition,
      employee_ids: employeeIds,
      start_date: startDateParam,
      end_date: endDateParam,
    }

    const url = '/shift_management/overlaps_shift_templates'

    return await api.get(url, params)
  }

  static maybeFetchCollection<T extends Model>(
    collection: Collection<T>,
    params: Filters | Record<string, unknown>,
    skip = false
  ) {
    return skip ? collection.toJS() : collection.rpcWithSet('', params)
  }

  static async fetchResources(params: FetchParams): Promise<FetchResourcesResponse> {
    const { startAt, endAt, page, perPage, filters, employeeIds, groupType, orderBy, orderDir } =
      params
    let filterParams: Filters = {
      start_at: startAt,
      end_at: endAt,
      ...filters,
      // Keep this filter with OR instead of {employee_ids: employeeIds, ...filters} to avoid overwriting
      // since no matter what the employeeIds has, the filters always have employee_ids
      employee_ids: employeeIds || filters.employee_ids,
      order_by: orderBy,
      order_dir: orderDir,
    }

    const isWorkAreaNthPage = groupType === GROUP_TYPES.areas && page != 1

    let workAreas = []
    let employeesPermissions = []
    if (hasFeature('dev_location_work_areas') && groupType === GROUP_TYPES.areas) {
      ;[workAreas, employeesPermissions] = await Promise.all([
        workAreasCollection.rpc('', { page, per_page: perPage, ...filterParams }),
        this.maybeFetchCollection(employeesPermissionsCollection, filterParams, isWorkAreaNthPage),
      ])
    } else {
      filterParams = { page, per_page: perPage, ...filterParams }
      employeesPermissions = await employeesPermissionsCollection.rpcWithSet('', filterParams)
    }

    const locationIds =
      groupType === GROUP_TYPES.areas
        ? uniq(workAreas.map((wa: WorkAreaPayload) => wa.location_id))
        : null

    if (employeesPermissions.length === 0) {
      return {
        employeesPermissions,
        leaves: [],
        planningVersions: [],
        shifts: [],
        holidays: [],
        restDays: [],
      }
    }

    const employeesIds = employeesPermissions.map((e: EmployeePermission) => e.id)
    const resourcesParams = { start_at: startAt, end_at: endAt, employee_ids: employeesIds }
    const skipRestRays = !hasFeature('shift_management_rest_day') || isWorkAreaNthPage
    const [leaves, planningVersions, shifts, restDays] = await Promise.all([
      this.maybeFetchCollection(leavesCollection, resourcesParams, isWorkAreaNthPage),
      this.maybeFetchCollection(planningVersionsCollection, resourcesParams, isWorkAreaNthPage),
      shiftsCollection.rpcWithSet('', {
        ...resourcesParams,
        shift_ids: filters.shift_ids,
        without_ids: filters.without_shifts_ids,
        location_ids: locationIds,
      }),
      this.maybeFetchCollection(restDaysCollection, resourcesParams, skipRestRays),
    ])

    // CompanyHolidays are fetched at the beginning, so we don't need to do
    // an extra request for getting them
    const employees = employeesCollection.filter((e) => employeesIds.includes(e.id))
    const holidays = companyHolidays.filterByRangeAndLocation(
      employees.map((e) => Number(e.attributes.location_id)),
      stringToDate(startAt, 'backend'),
      stringToDate(endAt, 'backend')
    )

    return { workAreas, employeesPermissions, leaves, planningVersions, shifts, holidays, restDays }
  }

  static async fetchEmployeesPermissions(params: DateParams): Promise<EmployeePermissions[]> {
    const filterParams: Filters = {
      start_at: params.startAt,
      end_at: params.endAt,
    }

    return await employeesPermissionsCollection.rpc('', filterParams)
  }

  static async fetchDefaultShifts(): Promise<DefaultShifts> {
    const defaultShiftsPayload = await defaultShiftsCollection.fetch()
    const defaultShifts = DefaultShifts.buildFromProps(defaultShiftsPayload)

    return defaultShifts
  }

  static async fetchTemplateWeeks(): Promise<TemplateWeeks> {
    const templateWeeksPayload = await templateWeeksCollection.fetch()
    const templateWeeks = TemplateWeeks.buildFromProps(templateWeeksPayload)

    return templateWeeks
  }

  static async fetchUnassignedShifts({
    features,
    startAt,
    endAt,
    view,
  }: UnassignedShiftParams): Promise<UnassignedShifts> {
    const hasCoverageView = features.canUseCoverageView
    const fetchUnassignedShifts = hasCoverageView && view.isByEmployees && view.isWeekly

    const unassignedShiftsPayload = fetchUnassignedShifts
      ? await unassignedShiftsCollection.fetch({
          data: { start_at: startAt, end_at: endAt },
        })
      : []

    const unassignedShifts = UnassignedShifts.buildFromProps(unassignedShiftsPayload)

    return unassignedShifts
  }
}

export default Api
